// ═══════════════════════════════════════════════════════════════════════════
// Optimistic UI — Domain Models
// Mirror the .NET DTOs from CalculationStateController / PbReservationService.
// ═══════════════════════════════════════════════════════════════════════════

export interface PlanningBlockDetail {
  id: string;
  info: string;                       // "Out ANTWERPEN → Unl UTRECHT"
  orderNumber: string;                // "2500865"
  customerSearchName: string;         // "CRONOS CENTRAL NV"
  containerOrTrailerSubType: string;
  trailerName?: string;
  containerName?: string;
  chassisName?: string;
  containerType?: string;
}

export type QueuedActionType = 'sendTo' | 'sendPb' | 'resendTo';
export type QueuedActionStatus = 'pending' | 'executing' | 'completed' | 'failed' | 'skipped';

export interface StartCalculationRequest {
  planningBlockIds: string[];
  userId: string;
  userName?: string;
  truckId?: string;
  truckName?: string;
  driverName?: string;
  sourceTruckName?: string;
  sourceDriverName?: string;
  planDate?: string;
  /** UI-only: overrides planDate for Live Planning Activity display (dots, "not on this date" grouping).
   *  Never sent to the backend — stripped in startCalculation. Use when the action's target day
   *  differs from the day-view default (e.g. week planning acting on a specific day of the week). */
  uiPlanDate?: string;
  operationType?: string; // "plan" | "move" | "assign" | "sendPb" | "cancelPb" | "unplanPb" | "cancelAndUnplanPb" | "replanPb" | "cancelAndReplanPb"
  planningBlockDetails?: PlanningBlockDetail[];
  queuedAction?: QueuedActionType;
}

export interface StartCalculationResponse {
  accepted: boolean;
  operationId?: string;
  conflicts?: CalculationConflict[];
  collisions?: PlanningCollision[];
}

export interface CalculationConflict {
  planningBlockId: string;
  lockedByUserId: string;
  lockedByUserName?: string;
  since: string;
}

export interface PlanningCollision {
  type: 'SlotBusy' | 'PbReserved' | 'PbInCalculation';
  blockingUserId: string;
  blockingUserName?: string;
  since: string;
  expiresAt?: string;
  userFriendlyMessage: string;
  truckName?: string;
  driverName?: string;
  operationType?: string;
  conflictingPbIds?: string[];
  conflictingOperationId?: string;
}

export interface CompleteCalculationRequest {
  operationId: string;
  result: CalculationResult;
}

export interface FailCalculationRequest {
  operationId: string;
  errorMessage: string;
  errorCode?: string;
}

export interface CalculationResult {
  success: boolean;
  createdTransportOrderId?: string;
  planningBlockOutcomes?: PbOutcome[];
  conflicts?: ConflictInfo[];
  warnings?: string[];
  errorMessage?: string;
  summary?: ResultSummary;
}

export interface ResultSummary {
  headline: string;         // "Pre-planned as requested" / "Pre-planned with changes"
  toId?: string;            // "TO26000100"
  toStatus?: string;        // "Pre-planned" / "Created"
  actualDate?: string;      // "09/02/2026"
  actualTruck?: string;     // "G4L018 1WWU238"
  actualDriver?: string;    // "Charité Doumbia"
  route?: string;           // "Out ANTWERPEN → Unl UTRECHT"
  customer?: string;        // "CRONOS CENTRAL NV"
  equipment?: string;       // "CNEU4511240 [40FT]"
  timeWindow?: string;      // "08:00 - 16:00"
  changes?: string[];       // ["Date changed: 09/02 → 10/02"]
  hasRejectedPbs?: boolean; // true when some PBs were not included in the result
}

export interface PbOutcome {
  pbId: string;
  status: string;           // "planned" | "date_changed" | "rejected" | "moved"
  description: string;
  transportOrderId?: string;
  dateChange?: string;      // "05/02 → 06/02"
}

export interface ConflictInfo {
  type: string;
  description: string;
  severity: string;         // "warning" | "error"
  resource?: string;        // e.g. "G4L036"
  resourceType?: string;    // e.g. "Truck" | "Driver"
  reason?: string;
}

export interface CalculationOperation {
  operationId: string;
  localOpId?: string; // Original local ID — preserved after server replaces operationId
  userId: string;
  userName?: string;
  operationType?: string;
  planningBlockIds: string[];
  planningBlockDetails?: PlanningBlockDetail[];
  truckId?: string;
  truckName?: string;
  driverName?: string;
  sourceTruckName?: string;
  sourceDriverName?: string;
  planDate?: string;
  /** UI-only display date; consumers should prefer this over planDate. Never sent to backend. */
  uiPlanDate?: string;
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
  expiresAt?: string;
  status: 'Calculating' | 'Completed' | 'Failed';
  verified?: boolean;
  result?: CalculationResult;
  queuedAction?: QueuedActionType | null;
  queuedActionStatus?: QueuedActionStatus | null;
  queuedActionError?: string | null;
  collisions?: PlanningCollision[];
}

// ── PB Reservation models ──

export interface PendingPbSelection {
  userId: string;
  userName?: string;
  pbs: PendingPb[];
  selectedAt: string;
  reservationExpiresAt?: string;
}

export interface PendingPb {
  id: string;
  orderNumber: string;
  selectionSequence?: number;
  info: string;
  customerSearchName?: string;
  containerName?: string;
  containerType?: string;
}

export interface ReservePbsRequest {
  userId: string;
  userName?: string;
  planningBlockIds: string[];
  reservedAt: string;
}

export interface PbReservation {
  userId: string;
  userName?: string;
  planningBlockIds: string[];
  reservedAt: string;
  expiresAt: string;
}
